/**
 * situation.ts (JARVIS SITUATIONAL AWARENESS)
 *
 * Two jobs, both read-only and both honest:
 *
 * 1. SITUATION BRIEF - one compact, spoken-plain paragraph describing what the
 *    operator's screen shows RIGHT NOW: the page identity, the client lens in
 *    scope, and the salient live figures the page itself published (network:
 *    down/affected counts + any active outage event; a subscriber open in a
 *    slide-out: that subscriber's state). Composed ONLY from the published
 *    context envelope (window.__ORBI_PLATFORM__ via platformContext, which is
 *    workspace-aware) - nothing is invented, nothing is scraped.
 *
 * 2. CAPABILITY CATALOGUE - the tools the agent can offer BY NAME in the
 *    current context. Config-driven and tenant-scoped: rows come from the
 *    jarvis_capabilities table (vertical-wide rows, overridable per client;
 *    a client-scoped row for the same capability wins, so a tenant can turn
 *    one off). A row only surfaces when its `requires` conditions hold
 *    against the live envelope (e.g. mass_resolve requires an active outage
 *    event on screen). The table maps capability -> tool NAMES; the tool
 *    DEFINITIONS live in code (useVapi), so config can never invent a tool.
 *
 * Both feed the same two moments:
 *   - call start: folded into the operator system prompt (_operator.situation
 *     / _operator.capabilities) so the agent knows the page before a word.
 *   - every in-place navigation (JARVIS NAV workspace): re-briefed as a silent
 *     context message the moment the new page's envelope lands, BEFORE the
 *     operator speaks.
 *
 * Flag law: the dock only calls into this when ORBI_JARVIS_NAV is on; the
 * capability tools additionally ride ORBI_GENERATIVE_UI (they paint preview
 * blocks). Kill either flag and the surface disappears end to end.
 *
 * No em-dashes; no internal codename in operator-facing strings.
 */

import { readPlatformContext, ON_SCREEN_LABELS, MONEY_LABELS, type PlatformContext } from './platformContext';

/* ── types ── */

export interface OutageEventRef {
  id: string;
  area_key?: string | null;
  area_label?: string | null;
  down_count?: number | null;
  total_in_area?: number | null;
  first_detected_at?: string | null;
}

export interface JarvisCapability {
  capability_key: string;
  title: string;
  /** How the agent should name and offer it, written for speech. */
  offer_line: string;
  /** Voice tool names this capability maps to (definitions live in code). */
  tool_names: string[];
  /** null = every section */
  section: string | null;
  /** Conditions on the live envelope, e.g. { active_outage: true }. */
  requires: Record<string, unknown>;
  client_id: string | null;
}

/* ── envelope readers (never invent) ── */

function num(v: unknown): number | null {
  return typeof v === 'number' && isFinite(v) ? v : null;
}

/** Active outage events the page published, newest-biggest first. */
export function readOutageEvents(ctx: PlatformContext): OutageEventRef[] {
  const raw = (ctx.on_screen as any)?.outage_events;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e: any) => e && e.id)
    .map((e: any) => ({
      id: String(e.id),
      area_key: e.area_key ?? null,
      area_label: e.area_label ?? null,
      down_count: num(e.down_count),
      total_in_area: num(e.total_in_area),
      first_detected_at: e.first_detected_at ?? null,
    }));
}

const n = (v: number) => v.toLocaleString('en-US');
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * The one-paragraph situation brief. Names only figures the page actually
 * published; a page with nothing wired yields just the page identity line,
 * never a claimed count.
 *
 * FAIL-SOFT (Jarvis regression fix): pre-context is an ENHANCEMENT. A missing,
 * empty, or partial envelope (e.g. the operator OVERVIEW altitude before the
 * async client reads land, where loadOutages never runs) must degrade to
 * silence, never throw - a throw here is what the operator hears as a generic
 * "server error" on navigation. So the whole composition is wrapped: any
 * unexpected shape yields '' and the caller simply sends no situation line,
 * rather than surfacing an error. Covers every nav path and call-start because
 * both consume THIS one function.
 */
export function situationBrief(ctx: PlatformContext): string {
  try {
    return composeSituationBrief(ctx);
  } catch {
    return '';
  }
}

function composeSituationBrief(ctx: PlatformContext): string {
  const os: any = (ctx && ctx.on_screen) || {};
  const scope: any = (ctx && ctx.scope) || {};
  const where = scope.altitude === 'client' && scope.client_name
    ? scope.client_name
    : scope.vertical_slug ? `${cap(scope.vertical_slug)} HQ (all clients)` : 'all clients';
  const section = scope.section || os.section || null;
  const parts: string[] = [];
  parts.push(`The operator's screen is now the ${section ? cap(String(section)) : 'dashboard'} view for ${where}.`);

  // Network health figures, in falling order of alarm.
  const down = num(os.down);
  const downTotal = num(os.down_total);
  if (down != null && down > 0) {
    parts.push(`Live: ${n(down)}${downTotal != null ? ` of ${n(downTotal)}` : ''} monitored units are DOWN on screen.`);
  }
  const bDown = num(os.buildings_down);
  if (bDown != null && bDown > 0) {
    parts.push(`${n(bDown)} building${bDown === 1 ? '' : 's'} unhealthy on the building poll.`);
  }
  const events = readOutageEvents(ctx);
  if (events.length) {
    const lines = events.slice(0, 3).map((e) => {
      const nm = e.area_label || e.area_key || 'unnamed area';
      const cnt = e.down_count != null ? `${n(e.down_count)}${e.total_in_area != null ? ` of ${n(e.total_in_area)}` : ''} customers down` : 'scale unknown';
      const since = e.first_detected_at ? `, detected ${String(e.first_detected_at).slice(0, 16).replace('T', ' ')} UTC` : '';
      return `${nm}: ${cnt}${since}`;
    });
    parts.push(`ACTIVE OUTAGE EVENT${events.length === 1 ? '' : 'S'} on this screen: ${lines.join('; ')}.`);
  }

  // Ordinary scale figures (only when nothing is burning leads with them).
  // ONE LABEL MAP (2026-08-26): render every envelope key the typed panel's
  // chips and "what am I looking at" answer know, via the same exported maps -
  // the brief used to know only the network/customers keys, leaving the voice
  // agent honestly blind on appointments/leads/costs/discovery pages while
  // the panel showed chips for exactly those figures.
  const scale: string[] = [];
  Object.keys(ON_SCREEN_LABELS).forEach((k) => {
    // account_count is the specific subscriber count; records is the generic
    // fallback - when both are present they are the same figure, say it once.
    if (k === 'records' && num(os.account_count) != null) return;
    if (num(os[k]) != null) scale.push(`${n(os[k])} ${ON_SCREEN_LABELS[k]}`);
  });
  Object.keys(MONEY_LABELS).forEach((k) => {
    const v = num(os[k]);
    if (v != null && v > 0) scale.push(`$${n(v)} ${MONEY_LABELS[k]}`);
  });
  if (scale.length) parts.push(`On screen: ${scale.join(', ')}.`);

  // The record open in a slide-out: that subscriber IS the subject.
  const rec: any = os.record_in_view;
  if (rec && (rec.name || rec.account_number)) {
    const bits: string[] = [];
    if (rec.status) bits.push(`status ${rec.status}`);
    if (rec.billing_status) bits.push(`billing ${rec.billing_status}`);
    if (rec.connection_status) bits.push(`connection ${rec.connection_status}`);
    if (rec.building_slug) bits.push(`building ${rec.building_slug}`);
    parts.push(`Open in a panel: ${rec.name || `account ${rec.account_number}`}${rec.account_number && rec.name ? ` (account ${rec.account_number})` : ''}${bits.length ? `; ${bits.join(', ')}` : ''}.`);
  }

  return parts.join(' ');
}

/* ── capability catalogue (config-driven, tenant-scoped) ── */

/**
 * Resolve the tenant's capability set from raw jarvis_capabilities rows.
 * The dock fetches rows for the vertical WITHOUT filtering `enabled`, so a
 * client-scoped row can override the vertical-wide row for the same
 * (capability_key, section): a client row that is enabled rewords/wins, a
 * client row with enabled=false REMOVES the capability for that tenant.
 * An absent table or a failed read yields [] upstream - capability surfacing
 * simply stays off; it never guesses.
 */
export function applyTenantOverrides(rows: any[], clientId: string | null): JarvisCapability[] {
  const keyOf = (r: any) => `${r.capability_key}|${r.section ?? ''}`;
  const base = new Map<string, any>();
  for (const r of rows) {
    if (!r || !r.capability_key) continue;
    if (r.client_id == null) {
      if (r.enabled !== false) base.set(keyOf(r), r);
    }
  }
  if (clientId) {
    for (const r of rows) {
      if (!r || !r.capability_key || r.client_id !== clientId) continue;
      if (r.enabled === false) base.delete(keyOf(r));
      else base.set(keyOf(r), r);
    }
  }
  return Array.from(base.values()).map((r: any) => ({
    capability_key: String(r.capability_key),
    title: String(r.title || r.capability_key),
    offer_line: String(r.offer_line || r.title || r.capability_key),
    tool_names: Array.isArray(r.tool_names) ? r.tool_names.map(String) : [],
    section: r.section ?? null,
    requires: r.requires && typeof r.requires === 'object' ? r.requires : {},
    client_id: r.client_id ?? null,
  }));
}

export interface CapabilityFlags {
  generative_ui: boolean;
}

/** Does a capability's `requires` hold against the live envelope + flags? */
function requirementsHold(c: JarvisCapability, ctx: PlatformContext, flags: CapabilityFlags): boolean {
  const req: any = c.requires || {};
  if (req.active_outage === true && readOutageEvents(ctx).length === 0) return false;
  if (req.generative_ui === true && !flags.generative_ui) return false;
  if (req.subject_open === true) {
    const rec: any = (ctx.on_screen as any)?.record_in_view;
    if (!rec || !(rec.name || rec.account_number)) return false;
  }
  return true;
}

/** The capabilities live HERE: current section (or section-less), requirements
 *  met. This is what the nav brief names and what the agent offers. */
export function capabilitiesHere(
  all: JarvisCapability[],
  ctx: PlatformContext,
  flags: CapabilityFlags
): JarvisCapability[] {
  const section = ctx.scope.section || (ctx.on_screen as any)?.section || null;
  return all.filter((c) =>
    (c.section == null || c.section === section) && requirementsHold(c, ctx, flags)
  );
}

/** One prompt/context block naming the catalogue. `hereOnly` renders the
 *  section-filtered live set (nav brief); otherwise the full tenant catalogue
 *  with its section conditions spelled out (call start, since the operator can
 *  navigate anywhere mid-call). */
export function capabilityLines(caps: JarvisCapability[], hereOnly: boolean): string {
  if (!caps.length) return '';
  const lines = caps.map((c) => {
    const tools = c.tool_names.length ? ` [tools: ${c.tool_names.join(' then ')}]` : '';
    const whereReq: string[] = [];
    if (!hereOnly && c.section) whereReq.push(`on the ${c.section} view`);
    if (!hereOnly && (c.requires as any)?.active_outage === true) whereReq.push('when an active outage event is on screen');
    const cond = whereReq.length ? ` (${whereReq.join(', ')})` : '';
    return `- ${c.title}${cond}: ${c.offer_line}${tools}`;
  });
  return lines.join('\n');
}

/* ── envelope settle: wait for the (framed) page's data to land ── */

/**
 * After an in-place navigation the target page needs a beat to fetch and
 * publish its envelope. Poll the workspace-aware read until the envelope
 * names the expected section (or carries any figure), then resolve; time out
 * to whatever is there so the brief still names the page identity honestly.
 */
export function waitForSituation(expectSection: string | null, timeoutMs = 7000): Promise<PlatformContext> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const ctx = readPlatformContext();
      const os: any = ctx.on_screen || {};
      const section = ctx.scope.section || os.section || null;
      const sectionOk = !expectSection || section === expectSection;
      const hasData = Object.keys(os).some((k) => k !== 'section');
      if ((sectionOk && hasData) || Date.now() - started > timeoutMs) {
        resolve(ctx);
        return;
      }
      setTimeout(tick, 250);
    };
    tick();
  });
}
